import { useEffect, useState } from 'react';
import {
  FiHeadphones, FiPhone, FiMail, FiGlobe, FiMapPin, FiClock,
  FiMessageCircle, FiAlertCircle, FiFacebook, FiInstagram, FiYoutube,
} from 'react-icons/fi';
import { PortalPage } from '../../components/layout/PortalPage';
import { SectionCard } from '../../components/layout/SectionCard';
import { Spinner } from '../../components/ui/Spinner';
import { supabase } from '../../lib/supabase';
import { getEstabelecimentoAtualId } from '../../lib/estabelecimento';

interface TelefoneUtil {
  nome: string;
  telefone: string;
}

interface CanaisAtendimento {
  whatsapp?: string;
  telefone_ouvidoria?: string;
  telefone_emergencia?: string;
  email_ouvidoria?: string;
  horario_ouvidoria?: string;
  horario_atendimento?: string;
  endereco_atendimento?: string;
  facebook?: string;
  instagram?: string;
  youtube?: string;
  telefones_uteis?: TelefoneUtil[];
}

interface DadosCanais {
  telefone: string | null;
  email_institucional: string | null;
  site: string | null;
  logradouro: string | null;
  numero: string | null;
  bairro: string | null;
  cidade: string | null;
  uf: string | null;
  canais: CanaisAtendimento;
}

/** Remove tudo que não é dígito para montar links tel:/wa.me. */
function soDigitos(v: string): string {
  return v.replace(/\D/g, '');
}

/** Garante protocolo em URLs digitadas sem http(s). */
function comProtocolo(url: string): string {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

export function CanaisAtendimentoPage() {
  const [dados, setDados] = useState<DadosCanais | null>(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    let vivo = true;
    supabase
      .from('estabelecimentos')
      .select('telefone, email_institucional, site, logradouro, numero, bairro, cidade, uf, configuracoes')
      .eq('id', getEstabelecimentoAtualId())
      .maybeSingle()
      .then(({ data }) => {
        if (!vivo) return;
        if (data) {
          const cfg = data.configuracoes as { canais_atendimento?: CanaisAtendimento } | undefined;
          setDados({
            telefone: data.telefone,
            email_institucional: data.email_institucional,
            site: data.site,
            logradouro: data.logradouro,
            numero: data.numero,
            bairro: data.bairro,
            cidade: data.cidade,
            uf: data.uf,
            canais: cfg?.canais_atendimento ?? {},
          });
        }
        setCarregando(false);
      });
    return () => { vivo = false; };
  }, []);

  if (carregando) {
    return (
      <PortalPage titulo="Canais de Atendimento" icone={<FiHeadphones size={22} />} largura="media">
        <div className="flex items-center justify-center py-20"><Spinner tamanho="lg" /></div>
      </PortalPage>
    );
  }

  const c = dados?.canais ?? {};
  const enderecoCompleto = [
    [dados?.logradouro, dados?.numero].filter(Boolean).join(', '),
    dados?.bairro,
    [dados?.cidade, dados?.uf].filter(Boolean).join(' - '),
  ].filter(Boolean).join(' · ');

  const telefonesUteis = (c.telefones_uteis ?? []).filter((t) => t.nome || t.telefone);
  const temRedes = c.facebook || c.instagram || c.youtube;

  return (
    <PortalPage
      titulo="Canais de Atendimento"
      subtitulo="Telefones, e-mails e endereços para falar com a prefeitura."
      icone={<FiHeadphones size={22} />}
      breadcrumbs={[{ label: 'Início', href: '/' }, { label: 'Canais de Atendimento' }]}
      largura="media"
    >
      <div className="space-y-5">
        <SectionCard titulo="Contato principal" icone={<FiPhone size={18} />}>
          <ul className="space-y-3">
            <ItemContato icone={<FiPhone size={16} />} rotulo="Telefone" valor={dados?.telefone} href={dados?.telefone ? `tel:${soDigitos(dados.telefone)}` : undefined} />
            <ItemContato
              icone={<FiMessageCircle size={16} />}
              rotulo="WhatsApp"
              valor={c.whatsapp}
              href={c.whatsapp ? `https://wa.me/55${soDigitos(c.whatsapp)}` : undefined}
              externo
            />
            <ItemContato icone={<FiMail size={16} />} rotulo="E-mail institucional" valor={dados?.email_institucional} href={dados?.email_institucional ? `mailto:${dados.email_institucional}` : undefined} />
            <ItemContato icone={<FiGlobe size={16} />} rotulo="Site" valor={dados?.site} href={dados?.site ? comProtocolo(dados.site) : undefined} externo />
            <ItemContato icone={<FiClock size={16} />} rotulo="Horário de atendimento" valor={c.horario_atendimento} />
            <ItemContato icone={<FiMapPin size={16} />} rotulo="Endereço de atendimento" valor={c.endereco_atendimento || enderecoCompleto || null} />
          </ul>
        </SectionCard>

        {(c.telefone_ouvidoria || c.email_ouvidoria || c.horario_ouvidoria) && (
          <SectionCard titulo="Ouvidoria" icone={<FiHeadphones size={18} />}>
            <ul className="space-y-3">
              <ItemContato icone={<FiPhone size={16} />} rotulo="Telefone da Ouvidoria" valor={c.telefone_ouvidoria} href={c.telefone_ouvidoria ? `tel:${soDigitos(c.telefone_ouvidoria)}` : undefined} />
              <ItemContato icone={<FiMail size={16} />} rotulo="E-mail da Ouvidoria" valor={c.email_ouvidoria} href={c.email_ouvidoria ? `mailto:${c.email_ouvidoria}` : undefined} />
              <ItemContato icone={<FiClock size={16} />} rotulo="Horário de funcionamento" valor={c.horario_ouvidoria} />
            </ul>
          </SectionCard>
        )}

        {c.telefone_emergencia && (
          <SectionCard titulo="Emergência / Plantão" icone={<FiAlertCircle size={18} />}>
            <ul className="space-y-3">
              <ItemContato icone={<FiAlertCircle size={16} />} rotulo="Telefone de emergência" valor={c.telefone_emergencia} href={`tel:${soDigitos(c.telefone_emergencia)}`} destaque />
            </ul>
          </SectionCard>
        )}

        {telefonesUteis.length > 0 && (
          <SectionCard titulo="Telefones úteis" icone={<FiPhone size={18} />}>
            <ul className="divide-y divide-slate-100">
              {telefonesUteis.map((t, i) => (
                <li key={i} className="flex items-center justify-between py-2.5">
                  <span className="text-sm text-slate-700">{t.nome || '—'}</span>
                  {t.telefone ? (
                    <a href={`tel:${soDigitos(t.telefone)}`} className="text-sm font-medium text-institutional hover:underline">{t.telefone}</a>
                  ) : (
                    <span className="text-sm text-slate-400">—</span>
                  )}
                </li>
              ))}
            </ul>
          </SectionCard>
        )}

        {temRedes && (
          <SectionCard titulo="Redes sociais" icone={<FiGlobe size={18} />}>
            <div className="flex flex-wrap gap-3">
              {c.facebook && <LinkRede icone={<FiFacebook size={16} />} rotulo="Facebook" href={comProtocolo(c.facebook)} />}
              {c.instagram && <LinkRede icone={<FiInstagram size={16} />} rotulo="Instagram" href={comProtocolo(c.instagram)} />}
              {c.youtube && <LinkRede icone={<FiYoutube size={16} />} rotulo="YouTube" href={comProtocolo(c.youtube)} />}
            </div>
          </SectionCard>
        )}
      </div>
    </PortalPage>
  );
}

function ItemContato({
  icone, rotulo, valor, href, externo, destaque,
}: {
  icone: React.ReactNode; rotulo: string; valor?: string | null; href?: string; externo?: boolean; destaque?: boolean;
}) {
  if (!valor) return null;
  return (
    <li className="flex items-start gap-3">
      <span className="mt-0.5 text-institutional">{icone}</span>
      <div className="min-w-0">
        <p className="text-xs text-slate-500">{rotulo}</p>
        {href ? (
          <a
            href={href}
            {...(externo ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            className={`break-words text-sm font-medium hover:underline ${destaque ? 'text-red-600' : 'text-institutional'}`}
          >
            {valor}
          </a>
        ) : (
          <p className="break-words text-sm text-slate-700">{valor}</p>
        )}
      </div>
    </li>
  );
}

function LinkRede({ icone, rotulo, href }: { icone: React.ReactNode; rotulo: string; href: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 transition hover:border-institutional hover:text-institutional"
    >
      {icone} {rotulo}
    </a>
  );
}
